import { type BaseItem, type NullablePartial } from '../common.js';
import { type AdvancedFhirBlock } from './advancedFhir/config.js';
import { type AuthorizationSubmissionBlock } from './authorizationSubmission/configs.js';
import type { BaseFlowBlock, BlockType, Duration, ProducedType } from './base/baseSchema.js';
import { type CareGapAlgorithmBlock } from './careGapAlgorithm/config.js';
import { type ConditionalBlock } from './conditional/config.js';
import { type ConversationBlock } from './conversation/configs.js';
import { type AgentConversationResponse } from './conversation/flowTypes.js';
import { type CreateEncounterSummaryBlock } from './createEncounterSummary/config.js';
import { type CreateNotableObjectBlock } from './createNotableObject/config.js';
import { type CreatePatientOrderBlock } from './createPatientOrder/config.js';
import { type FlowDecorationData } from './decorations/nodes.js';
import { type DocumentExtractBlock } from './documentExtract/config.js';
import { type DocumentSearchBlockConfig } from './documentSearch/configs.js';
import { type DocumentTemplateBlock } from './documentTemplate/configs.js';
import { type EvaluationBlock } from './evaluation/config.js';
import { type ExportExternalDocumentPacketBlockConfig } from './exportExternalDocumentPacket/configs.js';
import { type ExportTextToBucketBlock } from './exportTextToBucket/config.js';
import { type FaxBlock } from './fax/configs.js';
import { type FhirBlock } from './fhir/fhirBlocks.js';
import { type FileReference } from './file.js';
import { type FileConversionBlock } from './fileConversion/config.js';
import { type FilterListBlock } from './filterList/configs.js';
import { type BaseFlow, type Flow, type UpdateValues as FlowUpdateValues } from './flow.js';
import { type AgentCategory } from './flowAgent.js';
import { type FlowInputSchema } from './flowInputSchema.js';
import { type FormBlock } from './form/config.js';
import { type FormV2Block } from './form/configV2.js';
import { type GetExternalDocumentPacketBlockConfig } from './getExternalDocumentPacket/configs.js';
import { type GetFormSetBlockConfig } from './getFormSet/configs.js';
import { type GetInsurancesBlock } from './getInsurances/configs.js';
import { type GetNotableObjectBlock } from './getNotableObject/config.js';
import { type GetPatientOrderBlock } from './getPatientOrder/config.js';
import { type GetProviderBlock } from './getProvider/config.js';
import { type GetSftpFileBlock } from './getSftpFile/config.js';
import { type HccAlgorithmBlock } from './hccAlgorithm/hccAlgorithmBlock.js';
import { type HieGetDocumentsBlock } from './hieGetDocuments/config.js';
import { type HieQueryDocumentsBlock } from './hieQueryDocuments/config.js';
import { type Hl7Block } from './hl7/configs.js';
import { type HumanTaskBlock } from './humanTask/index.js';
import { type HumanTaskActivityReturnType } from './humanTask/returnType.js';
import { type InformationExtractionBlockConfig } from './informationExtraction/configs.js';
import { type InsuranceCardBlock } from './insuranceCard/config.js';
import { type IteratorV1Block } from './iterator/v1/config.js';
import { type IteratorV2Block } from './iterator/v2/config.js';
import { type LlmPromptBlock } from './llmPrompt/config.js';
import { type MedicalNecessityCheckBlockConfig } from './medicalNecessityCheck/configs.js';
import { type MirthBlock } from './mirth/config.js';
import { type MontageOnBaseBlock } from './montageOnBase/config.js';
import { type OcrBlock, type OcrV2Block } from './ocr/config.js';
import { type OcrDetailResponse } from './ocr/flowTypes.js';
import { type OutputBlock } from './output/config.js';
import { type PatientDataStoreBlockConfig } from './patientDataStore/config.js';
import type * as PatientDataStoreTypes from './patientDataStore/flowTypes.js';
import { type RealTimeEligibilityBlock } from './realTimeEligibility/config.js';
import { type RepeatBlock } from './repeat/config.js';
import { type RestBlock } from './rest/config.js';
import { type RestApiClassicAuthorization } from './restApiClassicAuthorization/config.js';
import { type SchedulingBlock } from './scheduling/config.js';
import { type ScriptedFlowBlock } from './script/config.js';
import { type SendEmailBlock, type SendEmailBlockOutput } from './sendEmail/config.js';
import { type SendSmsBlock } from './sendSms/config.js';
import { type ServiceReviewBlock } from './serviceReview/config.js';
import { type SkillV2Block } from './skill/index.js';
import { type StartBlock } from './start/config.js';
import { type SubFlowV1Block } from './subFlow/v1/config.js';
import { type SubFlowV2Block } from './subFlow/v2/config.js';
import { type SwitchBlock } from './switch/config.js';
import { type TextBlock } from './text/config.js';
import { type UpdateFlowConfigBlock } from './updateFlowConfig/configs.js';
import { type UpdateNotableObjectBlock } from './updateNotableObject/config.js';
import {
  type VoiceCallBlockConfig,
  type VoiceCallReturnData,
  type VoiceCallV2ReturnData,
} from './voiceCall/config.js';
import { type VoiceCallV2Block } from './voiceCall/configV2.js';
import { type WaitBlock, type WaitBlockV2 } from './wait/index.js';
import { type WaitForFormCompletionBlock } from './waitForFormCompletion/config.js';
import { type WaitForSignalBlock } from './waitForSignal/config.js';
import { type EncounterSummary } from '../encounter/summary.js';
import { type Encounter } from '../encounter.js';
import { type ExternalDocumentPacket } from '../externalDocumentPacket.js';
import { type Finding } from '../patient/finding.js';
import { type BasePatient, type Sex } from '../patient.js';
import type { Kind } from '../rpaJob.js';
import type { User } from '../user.js';
import { type GenerateFileBlock } from './generateFile/configs.js';

export * from './fhir/fhirBlocks.js';
export { NotableObject } from './base/baseSchema.js';
export * from './advancedFhir/config.js';
export * from './authorizationSubmission/configs.js';
export * from './createEncounterSummary/config.js';
export * from './conversation/configs.js';
export * from './documentSearch/configs.js';
export * from './documentTemplate/configs.js';
export * from './documentExtract/config.js';
export * from './exportTextToBucket/config.js';
export * from './evaluation/config.js';
export * from './exportExternalDocumentPacket/configs.js';
export * from './fax/configs.js';
export * from './filterList/configs.js';
export * from './form/config.js';
export * from './form/configV2.js';
export * from './fileConversion/config.js';
export * from './getFormSet/configs.js';
export * from './getExternalDocumentPacket/configs.js';
export * from './getInsurances/configs.js';
export * from './getPatientOrder/config.js';
export * from './getProvider/config.js';
export * from './hccAlgorithm/hccAlgorithmBlock.js';
export * as HieGetDocuments from './hieGetDocuments/config.js';
export * from './hieQueryDocuments/config.js';
export * from './hl7/configs.js';
export * from './humanTask/index.js';
export * from './informationExtraction/configs.js';
export * from './insuranceCard/config.js';
export * from './knowledgeBaseFile.js';
export * from './llmPrompt/config.js';
export * from './mirth/config.js';
export * from './montageOnBase/config.js';
export * from './repeat/config.js';
export * from './rest/config.js';
export * from './scheduling/config.js';
export * from './script/config.js';
export * from './sendEmail/config.js';
export * from './sendSms/config.js';
export * from './serviceReview/config.js';
export * from './subFlow/v1/config.js';
export * from './subFlow/v2/config.js';
export * from './subFlow/v2/flowArgument.js';
export * from './subFlow/v2/dynamicValue.js';
export * from './skill/index.js';
export * from './switch/config.js';
export * from './start/config.js';
export * from './text/config.js';
export * from './updateFlowConfig/configs.js';
export * from './updateNotableObject/config.js';
export * from './createNotableObject/config.js';
export * from './createPatientOrder/config.js';
export * from './iterator/v1/config.js';
export * from './iterator/v2/config.js';
export * from './getNotableObject/config.js';
export * from './output/config.js';
export * from './restApiClassicAuthorization/config.js';
export * from './careGapAlgorithm/config.js';
export * from './medicalNecessityCheck/configs.js';
export * as PatientDataStore from './patientDataStore/config.js';
export * from './base/baseSchema.js';
export * from './base/communicationWindow.js';
export * from './base/keywords.js';
export * from './ocr/config.js';
export * from './conversation/configs.js';
export * from './getSftpFile/config.js';
export * from './realTimeEligibility/config.js';
export * from './decorations/index.js';
export { FlowVersionTarget } from './flowExecution.js';
export * from './flowInputSchema.js';
export * from './voiceCall/modelDescriptions.js';
export * from './voiceCall/config.js';
export * from './voiceCall/configV2.js';
export * from './voiceCall/transferToolConfig.js';
export * from './wait/index.js';
export * from './waitForSignal/config.js';
export * from './waitForFormCompletion/config.js';
export * as Conditional from './conditional/index.js';
export * as GenerateFile from './generateFile/configs.js';
export * from './blockCompatibility/index.js';

export enum FlowDefinitionState {
  Draft = 'Draft',
  Review = 'Review',
  Published = 'Published',
  Superseded = 'Superseded',
  SoftDeleted = 'SoftDeleted',
}

export const FlowDefinitionStateProps: Record<
  FlowDefinitionState,
  {
    name: string;
    isEditable: boolean;
    isPrePublishState: boolean;
    isCurrentState: boolean;
  }
> = {
  [FlowDefinitionState.Draft]: {
    name: 'Draft',
    isEditable: true,
    isPrePublishState: true,
    isCurrentState: true,
  },
  [FlowDefinitionState.Review]: {
    name: 'Review',
    isEditable: false,
    isPrePublishState: true,
    isCurrentState: true,
  },
  [FlowDefinitionState.Published]: {
    name: 'Published',
    isEditable: false,
    isPrePublishState: false,
    isCurrentState: true,
  },
  [FlowDefinitionState.Superseded]: {
    name: 'Superseded',
    isEditable: false,
    isPrePublishState: false,
    isCurrentState: false,
  },
  [FlowDefinitionState.SoftDeleted]: {
    name: 'Deleted',
    isEditable: false,
    isPrePublishState: false,
    isCurrentState: false,
  },
};

export interface FlowData {
  blocks: FlowBlock[];
  edges: FlowEdge[];
  decorations?: FlowDecorationData;
  inputSchema?: FlowInputSchema;
}

export interface FlowTag {
  name: string;
  category?: string | null;
}

interface BaseFlowDefinition {
  flowId: string;
  version: number;
  state: FlowDefinitionState;
  /**
   * Per-version creator: the user who started this specific FlowDefinition
   * (e.g. the user who cloned the previous published version into a new draft).
   * Distinct from `Flow.createdById`, which records the original Flow's creator
   * and does not change across versions.
   */
  createdById: string | null;
  /**
   * Per-version last updater: the user who most recently mutated this
   * FlowDefinition. Distinct from `Flow.updatedById`, which only advances when
   * Flow-level fields (name, description, organizationId, agentId, tags) change.
   */
  updatedById: string | null;
  data: FlowData;
  lastSnapshotId: string | null; // FK to snapshots, set to NULL when data is mutated
  notes: string | null;
  documentationMarkdown?: string | null;
}

export interface FlowDefinition extends BaseItem, BaseFlowDefinition {}

export interface FlowDefinitionWithUsers extends FlowDefinition {
  /** Email of the per-version {@link BaseFlowDefinition.createdById}. */
  createdByEmail: string | null;
  /** Email of the per-version {@link BaseFlowDefinition.updatedById}. */
  updatedByEmail: string | null;
}

/**
 * This is a temporary interface to allow us to migrate to separating Flows from FlowDefinitions.
 */
export interface FlowDefinitionWithFlow extends FlowDefinitionWithUsers, Omit<Flow, 'state'> {}

export type FlowDefinitionWithFlowBasic = Omit<FlowDefinitionWithFlow, 'data'>;

export type SubflowDefinitionWithFlow = Pick<
  FlowDefinitionWithFlow,
  | 'id'
  | 'flowId'
  | 'version'
  | 'name'
  | 'description'
  | 'kind'
  | 'state'
  | 'organizationId'
  | 'notes'
  | 'documentationMarkdown'
  | 'data'
>;

export interface SkillDefinitionWithSkill {
  skill: Pick<Flow, 'id' | 'name' | 'description' | 'kind'>;
  flowDefinitions: SubflowDefinitionWithFlow[];
}

export interface FlowDefinitionForList extends FlowDefinitionWithFlow {
  runs: number;
}

interface CreateValuesWithFlow extends Omit<BaseFlowDefinition, 'flowId' | 'version' | 'state'> {
  // The omitted fields get set to defaults on create
  tags?: FlowTag[] | null;
}

type BaseFlowSansStateAndAgentId = Omit<BaseFlow, 'state' | 'agentId'>;
export interface UpdateValuesWithFlow
  extends
    Omit<FlowUpdateValues, 'state' | 'kind' | 'updatedById' | 'clonedFromFlowId'>,
    Pick<Partial<BaseFlowDefinition>, 'data' | 'notes' | 'state' | 'documentationMarkdown'> {}

/**
 * Defining an interface for creating flow def + flow together until versioning is worked out.
 * flowId is not specified because we create it in {@link createFlowWithDefinition}
 **/

// Flow creation with existing agent
export type CreateFlowWithDefinitionValuesExistingAgent = NullablePartial<CreateValuesWithFlow> &
  NullablePartial<BaseFlowSansStateAndAgentId> & {
    agentId: string | null;
  };

// Flow creation with new agent
export type CreateFlowWithDefinitionValuesNewAgent = NullablePartial<CreateValuesWithFlow> &
  NullablePartial<BaseFlowSansStateAndAgentId> & {
    agent: {
      name: string;
      description?: string;
      category?: AgentCategory;
      organizationId: string;
      state?: string;
    };
  };

// Union type for both valid payloads
export type CreateFlowWithDefinitionValues =
  CreateFlowWithDefinitionValuesExistingAgent | CreateFlowWithDefinitionValuesNewAgent;

export type FlowDefinitionFindAllOptions = Partial<{
  name: string; // checked by ilike
  ids: string[];
  organizationId: string | string[];
  createdBefore: string;
  createdAfter: string;
  createdByEmail: string;
  agentId: string;
  agentIds: string[];
  updatedByEmail: string;
  state: FlowDefinitionState;
  includeInactive: boolean;
  flowId: string;
  version: number;
  includesSubFlowDefinitionId: string;
  limit: number;
  offset: number;
  orderBy: string;
  orderDirection: 'asc' | 'desc';
  orderSpec: string;
  snapshotIds: string[];
}>;

export type FlowBlock =
  | AdvancedFhirBlock
  | AsynchronousExportJobBlock
  | AsynchronousExtractJobBlock
  | CallbackBlock
  | ConditionalBlock
  | CareGapAlgorithmBlock
  | ConversationBlock
  | CreateEncounterSummaryBlock
  | CreateNotableObjectBlock
  | CreatePatientOrderBlock
  | DocumentExtractBlock
  | DocumentSearchBlock
  | DocumentTemplateBlock
  | EvaluationBlock
  | ExportExternalDocumentPacketBlock
  | ExportTextToBucketBlock
  | FaxBlock
  | FhirBlock
  | FileConversionBlock
  | FilterListBlock
  | FormBlock
  | FormV2Block
  | GenerateFileBlock
  | GetExternalDocumentPacketBlock
  | GetFormSetBlock
  | GetInsurancesBlock
  | GetPatientBlock
  | GetPatientOrderBlock
  | GetNotableObjectBlock
  | GetProviderBlock
  | GetSftpFileBlock
  | RealTimeEligibilityBlock
  | HccAlgorithmBlock
  | HieGetDocumentsBlock
  | HieQueryDocumentsBlock
  | Hl7Block
  | HumanTaskBlock
  | InformationExtractionBlock
  | InsuranceCardBlock
  | IteratorV1Block
  | IteratorV2Block
  | RepeatBlock
  | LlmPromptBlock
  | MedicalNecessityCheckBlock
  | MirthBlock
  | MontageOnBaseBlock
  | OcrBlock
  | OcrV2Block
  | OutputBlock
  | PatientDataStoreBlock<ProducedType>
  | RestBlock
  | RestApiClassicAuthorization
  | SchedulingBlock
  | ScriptedFlowBlock
  | SendEmailBlock
  | SendSmsBlock
  | ServiceReviewBlock
  | StartBlock
  | SubFlowV1Block
  | SubFlowV2Block
  | SkillV2Block
  | SwitchBlock
  | TextBlock
  | UpdateFlowConfigBlock
  | UpdateNotableObjectBlock
  | VoiceCallBlock
  | VoiceCallV2Block
  | VoiceTranscribeBlock
  | AuthorizationSubmissionBlock
  | WaitBlock
  | WaitBlockV2
  | WaitForSignalBlock
  | WaitForFormCompletionBlock;

export interface ResponseFormatField {
  fieldLabel: string;
  definition: string;
}

// In backend ConsumerOf includes a consumesType field
export interface ConsumerOf {
  sourceBlockName: string;
}

export interface PatientDataStoreBlock<T extends ProducedType> extends BaseFlowBlock<T> {
  type: BlockType.PatientDataStore;
  config: PatientDataStoreBlockConfig;
}

export interface ProducedTypeMap {
  [ProducedType.Any]: any;
  [ProducedType.Start]: StartBlockOutput;
  [ProducedType.SubFlowV1Result]: SubFlowBlockData;
  [ProducedType.Patient]: PatientWithEhrInformationId;
  [ProducedType.LabValues]: { data: unknown[] };
  [ProducedType.EncounterSummary]: EncounterSummary;
  [ProducedType.ExternalDocumentPacket]: ExternalDocumentPacket;
  [ProducedType.Practitioner]: Record<string, unknown>;
  [ProducedType.Provider]: User;
  [ProducedType.Encounter]: Encounter;
  [ProducedType.Encounters]: Record<string, unknown>;
  [ProducedType.Appointments]: Record<string, unknown>;
  [ProducedType.ServiceRequests]: Record<string, unknown>;
  [ProducedType.Conditions]: { data: unknown[] };
  [ProducedType.Medications]: { data: unknown[] };
  [ProducedType.DocumentMetadata]: { data: unknown[] };
  [ProducedType.DocumentBinaries]: { data: unknown[] };
  [ProducedType.DocumentExtract]: unknown;
  [ProducedType.SurgicalHistories]: { data: unknown[] };
  [ProducedType.PatientFindings]: Finding[];
  [ProducedType.VoiceCall]: VoiceCallReturnData;
  [ProducedType.VoiceCallV2]: VoiceCallV2ReturnData;
  [ProducedType.VoiceTranscribe]: Record<string, unknown>;
  [ProducedType.Evaluation]: Record<string, unknown>;
  [ProducedType.Coverages]: Record<string, unknown>;
  [ProducedType.PdsLabValues]: {
    data: PatientDataStoreTypes.LabValueResponse[];
  };
  [ProducedType.PdsEncounters]: {
    data: PatientDataStoreTypes.EncounterResponse[];
  };
  [ProducedType.PdsMedications]: {
    data: PatientDataStoreTypes.MedicationResponse[];
  };
  [ProducedType.PdsDocumentMetadata]: {
    data: PatientDataStoreTypes.DocumentMetadataResponse[];
  };
  [ProducedType.PdsDocumentBinaries]: {
    data: PatientDataStoreTypes.DocumentResponse[];
  };
  [ProducedType.PdsDocumentTranscriptions]: {
    data: PatientDataStoreTypes.DocumentTranscriptionResponse[];
  };
  [ProducedType.PdsSurgicalHistories]: {
    data: PatientDataStoreTypes.SurgicalHistoryResponse[];
  };
  [ProducedType.PdsConditions]: {
    data: PatientDataStoreTypes.ConditionResponse[];
  };
  [ProducedType.FileConversion]: string | { fileReferences: string[] };
  [ProducedType.FileReference]: FileReference;
  [ProducedType.OcrDetailResponse]: OcrDetailResponse;
  [ProducedType.AgentConversation]: AgentConversationResponse;
  [ProducedType.HumanTask]: HumanTaskActivityReturnType;
  [ProducedType.SendEmail]: SendEmailBlockOutput;
}

export interface SubFlowBlockData {
  result?: any;
}

export interface GetFormSetBlock extends BaseFlowBlock {
  type: BlockType.GetFormSet;
  config: GetFormSetBlockConfig;
}

export interface GetPatientBlock extends BaseFlowBlock<ProducedType.Patient> {
  type: BlockType.GetPatient;
  config: {
    returnType: ProducedType.Patient;
  };
}

export interface GetExternalDocumentPacketBlock extends BaseFlowBlock<ProducedType.ExternalDocumentPacket> {
  type: BlockType.GetExternalDocumentPacket;
  config: GetExternalDocumentPacketBlockConfig;
}

export interface ExportExternalDocumentPacketBlock extends BaseFlowBlock {
  type: BlockType.ExportExternalDocumentPacket;
  config: ExportExternalDocumentPacketBlockConfig;
}

export interface VoiceCallBlock extends BaseFlowBlock {
  type: BlockType.VoiceCall;
  config: VoiceCallBlockConfig;
}

export interface CallbackBlock extends BaseFlowBlock {
  type: BlockType.Callback;
  config: {
    message: string;
    waitTime: Duration;
    timeout: Duration;
  };
}

export interface InformationExtractionBlock extends BaseFlowBlock {
  type: BlockType.InformationExtraction;
  config: InformationExtractionBlockConfig;
}

export interface DocumentSearchBlock extends BaseFlowBlock {
  type: BlockType.DocumentSearch;
  config: DocumentSearchBlockConfig;
}

export interface VoiceTranscribeBlock extends BaseFlowBlock {
  type: BlockType.VoiceTranscribe;
  config: {
    storagePath: string;
    transcriptionType: string;
  };
}

export interface MedicalNecessityCheckBlock extends BaseFlowBlock {
  type: BlockType.MedicalNecessityCheck;
  config: MedicalNecessityCheckBlockConfig;
}

export interface FlowEdge {
  source: string;
  target: string;

  /** When an edge is from a Condition to a block, it defines the path for the condition value it represents **/
  condition?: string;

  /** True when an edge exists as the error path **/
  isError?: boolean;
}

export interface AsynchronousExtractJobBlock extends BaseFlowBlock {
  type: BlockType.AsynchronousExtractJob;
  config: {
    jobKind: Kind;
    timeout: Duration;
  };
}

export interface AsynchronousExportJobBlock extends BaseFlowBlock {
  type: BlockType.AsynchronousExportJob;
  config: {
    timeout: Duration;
    v2?: boolean;
  } & (
    | {
        jobKind: Kind.ExportPrevisitPlanning;
        payload: {
          content: string;
          patientEhrId: string;
          patientFirstName: string;
          patientLastName: string;
          patientDateOfBirth: Date;
          patientSex: Sex;
          encounterEhrReferenceValue: string;
        };
      }
    | {
        jobKind: Kind.ExportDummyValues;
        payload: {
          foo: string;
        };
      }
  );
}

export interface PatientWithEhrInformationId extends BasePatient {
  ehrInformationId: string;
}

export interface StartBlockOutput {
  organizationId: string;
  practiceId: string;
  entityType?: string;
  entityId?: string;
  entity?: unknown;
  startData?: unknown;
}

export interface CycleDetection {
  sourceBlock: FlowBlock;
  cyclicBlock: FlowBlock;
}

export interface CodeMirrorCompletionSnippet {
  label: string;
  type?: string;
  boost: number;
}
